import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Button,
  BUTTON_VARIANTS,
  IconAddCircle,
  IconFileDownload,
  TabPanel,
  Tab,
  Tabs,
  Toast,
  MultiToasts,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
} from 'cdk-radial';
import './OrderDetails.css';
import Page from '../../page';
import { APIs } from './Tabs/APIs/APIs';
import { Utilization } from './Tabs/Utilization';
import Metrics from './Tabs/Metrics';
import APIErrors from './Tabs/APIErrors';
import { History } from './Tabs/History';
import MyCart from '../MyCart/MyCart';
import styled from 'styled-components';
import { clearToastNotification, clearAddBundleLocationsState, clearRemoveBundleLocationsState } from '../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../utils/amplitude';
import { AMPLITUDE_EVENTS } from '../../../constants/amplitudeConstants';
import config from '../../../utils/config';
import { triggerUserDownload } from '../../../utils/utils';
import { PAGE_TITLE } from '../../../constants/constants';
import { trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS } from '../../../utils/gtm';
import { setAPIDirectoryPage } from '../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';

const subscriptionsUrl = config.url.subscriptionUrl;
const API_DIR_URL = 'api-directory';
const MY_APIS_URL = '/my-apis';

const BUNDLE_LOCATION_TOAST_MESSAGES = {
  ADD_success:    (name) => <div>Your request for add locations to <b>{name}</b> bundle has been successful.</div>,
  ADD_failure:    (name) => <div>Your request for add locations to <b>{name}</b> bundle has failed.</div>,
  REMOVE_success: (name) => <div>Your request to remove locations from <b>{name}</b> bundle has been successful.</div>,
  REMOVE_failure: (name) => <div>Your request to remove locations from <b>{name}</b> bundle has failed.</div>,
  BOTH_success:   (name) => <div>Your request for <b>{name}</b> bundle has been successful.</div>,
  BOTH_failure:   (name) => <div>Your request for <b>{name}</b> bundle has failed.</div>,
};

const getBundleLocationToasts = (
  { isAddBundleLocationsSuccess, isAddBundleLocationsErrored, isRemoveBundleLocationsSuccess, isRemoveBundleLocationsErrored },
  bundleName
) => {
  const SUCCESS = 'success';
  const FAILURE = 'failure';
  const addResult = isAddBundleLocationsSuccess ? SUCCESS : isAddBundleLocationsErrored ? FAILURE : null;
  const removeResult = isRemoveBundleLocationsSuccess ? SUCCESS : isRemoveBundleLocationsErrored ? FAILURE : null;

  if (!addResult && !removeResult) return [];

  const positive = (msg) => ({ variant: TOAST_VARIANTS.POSITIVE, content: msg });
  const negative = (msg) => ({ variant: TOAST_VARIANTS.NEGATIVE, content: msg });
  const toastVariant = (result, msg) => result === SUCCESS ? positive(msg) : negative(msg);
  const getMessage = (op, result) => BUNDLE_LOCATION_TOAST_MESSAGES[`${op}_${result}`](bundleName);

  if (addResult && removeResult && addResult === removeResult)
    return [toastVariant(addResult, getMessage('BOTH', addResult))];

  const toasts = [];
  if (addResult) toasts.push(toastVariant(addResult, getMessage('ADD', addResult)));
  if (removeResult) toasts.push(toastVariant(removeResult, getMessage('REMOVE', removeResult)));
  return toasts;
};

const OrderDetailsTabContainer = (props) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const state = useSelector((state) => state);
  const orders = state?.orderAPISlice.orders;

  const TAB_INDICES = {
    APIS: 0,
    UTILIZATION: 1,
    METRICS: 2,
    API_ERRORS: 3,
    HISTORY: 4,
  };

  const isDisabled =
    Object.keys(orders?.app || {}).length === 0 &&
    orders?.restApis.length === 0 &&
    orders?.asyncApis.length === 0;

  const CustomStyledTab = isDisabled
    ? styled(Tab)`
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
      `
    : styled(Tab)``;

  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice,
  );

  const handleTabChange = (tabIndex) => {
    setActiveItemIndex(tabIndex);

    let subsection = '';
    switch (tabIndex) {
      case TAB_INDICES.APIS:
        subsection = 'APIs';
        sendAmplitudeData(AMPLITUDE_EVENTS.MY_APIS, orgAmplitudeProperties);
        break;
      case TAB_INDICES.UTILIZATION:
        subsection = 'Utilization';
        sendAmplitudeData(
          AMPLITUDE_EVENTS.MY_APIS_UTILIZATION,
          orgAmplitudeProperties,
        );
        break;
      case TAB_INDICES.METRICS:
        subsection = 'Metrics';
        sendAmplitudeData(
          AMPLITUDE_EVENTS.MY_APIS_METRICS,
          orgAmplitudeProperties,
        );
        break;
      case TAB_INDICES.API_ERRORS:
        subsection = 'API Errors';
        sendAmplitudeData(
          AMPLITUDE_EVENTS.MY_APIS_API_ERRORS,
          orgAmplitudeProperties,
        );
        break;
      case TAB_INDICES.HISTORY:
        subsection = 'History';
        break;
      default:
        break;
    }
  };

  return (
    <div className="order-details-page-tabs">
      <Tabs
        activeItemIndex={activeItemIndex}
        items={[
          {
            id: 'tab-apis',
            panelId: 'panel-apis',
            dataTestId: 'panel-apis',
            renderPanel: (tabProps) => (
              <TabPanel {...tabProps} className="order-details-tab-panel">
                <APIs />
              </TabPanel>
            ),
            renderTab: (tabProps) => (
              <Tab
                label="APIs"
                className="tabStyle"
                hideLabel={false}
                {...tabProps}
              />
            ),
          },
          {
            id: 'tab-api-utilization',
            panelId: 'panel-api-utilization',
            dataTestId: 'panel-api-utilization',
            renderPanel: (tabProps) => (
              <TabPanel {...tabProps} className="order-details-tab-panel">
                <Utilization />
              </TabPanel>
            ),
            renderTab: (tabProps) => (
              <CustomStyledTab
                label="Utilization"
                className="tabStyle"
                hideLabel={false}
                {...tabProps}
              />
            ),
          },
          {
            id: 'tab-api-metrics',
            panelId: 'panel-api-metrics',
            dataTestId: 'panel-api-metrics',
            renderPanel: (tabProps) => (
              <TabPanel {...tabProps} className="order-details-tab-panel">
                <Metrics />
              </TabPanel>
            ),
            renderTab: (tabProps) => (
              <CustomStyledTab
                label="Metrics"
                className="tabStyle"
                hideLabel={false}
                {...tabProps}
              />
            ),
          },
          {
            id: 'tab-api-errors',
            panelId: 'panel-api-errors',
            dataTestId: 'panel-api-errors',
            renderPanel: (tabProps) => (
              <TabPanel {...tabProps} className="order-details-tab-panel">
                <APIErrors />
              </TabPanel>
            ),
            renderTab: (tabProps) => (
              <CustomStyledTab
                label="API Errors"
                className="tabStyle"
                hideLabel={false}
                {...tabProps}
              />
            ),
          },
        ]}
        onActiveItemChange={handleTabChange}
        tabsLabel="Tabs group description"
      />
    </div>
  );
};

const OrderDetails = () => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { authenticationToken: token } = state?.loginDetailsSlice;
  const { subscribeRequestSent } = state?.daabSubscriptionsSlice;
  const {
    errorUpdatingOrder,
    webhookUpdated,
    orders,
    apiDeleted,
    errorDeletingApi,
    deletedApiName,
    lastFetched,
    bundleDeleted,
    deletedBundleName,
    isAddBundleLocationsSuccess,
    isAddBundleLocationsErrored,
    isRemoveBundleLocationsSuccess,
    isRemoveBundleLocationsErrored
  } = state?.orderAPISlice;
  const bundleName = state?.uiFactorySlice?.additionalData?.bundleName;
  const [showCart, setShowCart] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isDownloadReportLoading, setIsDownloadReportLoading] = useState(false);
  const [toastVariant, setToastVariant] = useState(null);
  const [toastContent, setToastContent] = useState('');
  const hasTrackedPageView = useRef(false);
  const [toasts, setToasts] = useState([]);

  const removeToast = (toastId) => {
    setToasts((prevState) => prevState.filter((t) => t.id !== toastId));
  };

  const addToast = (toastDef) => {
    const id = toastDef.id || `${Math.random()}_${Date.now()}`.substring(2);
    const def = {
      visibilityDuration: TOAST_VISIBILITY_DURATIONS.SIX_SECONDS,
      ...toastDef,
      id,
      onVisibilityDurationEnd: toastDef.onVisibilityDurationEnd || (() => removeToast(id)),
    };
    setToasts((prevState) => [def, ...prevState]);
  };

  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice,
  );

  useEffect(() => {
    if (
      webhookUpdated ||
      errorUpdatingOrder ||
      subscribeRequestSent ||
      apiDeleted ||
      errorDeletingApi ||
      bundleDeleted
    ) {
      if (subscribeRequestSent) {
        setToastVariant(TOAST_VARIANTS.POSITIVE);
        setToastContent('Request for API Subscription is successful');
      } else if (webhookUpdated) {
        setToastVariant(TOAST_VARIANTS.POSITIVE);
        setToastContent('Successfully saved webhook');
      } else if (apiDeleted) {
        setToastVariant(TOAST_VARIANTS.POSITIVE);
        setToastContent(
          `Your request to delete ${deletedApiName} has been successful.`,
        );
      } else if (bundleDeleted) {
        setToastVariant(TOAST_VARIANTS.POSITIVE);
        setToastContent(
          `Your request to delete ${deletedBundleName} has been received. Bundle integration deletion has been initiated. Changes may take a few minutes to appear.`,
        );
      } else if (errorDeletingApi) {
        setToastVariant(TOAST_VARIANTS.NEGATIVE);
        setToastContent(`Failed to delete ${deletedApiName}`);
      } else {
        setToastVariant(TOAST_VARIANTS.NEGATIVE);
        setToastContent('Failed to save webhook');
      }
      setShowToast(true);
      dispatch(clearToastNotification());
    }
  }, [
    dispatch,
    webhookUpdated,
    errorUpdatingOrder,
    subscribeRequestSent,
    apiDeleted,
    errorDeletingApi,
    bundleDeleted,
    deletedApiName,
    deletedBundleName,
  ]);

  useEffect(() => {
    const newToasts = getBundleLocationToasts(
      { isAddBundleLocationsSuccess, isAddBundleLocationsErrored, isRemoveBundleLocationsSuccess, isRemoveBundleLocationsErrored },
      bundleName
    );
    if (newToasts.length === 0) return;

    newToasts.forEach((t) => addToast(t));
    dispatch(clearAddBundleLocationsState());
    dispatch(clearRemoveBundleLocationsState());
  }, [isAddBundleLocationsSuccess, isAddBundleLocationsErrored, isRemoveBundleLocationsSuccess, isRemoveBundleLocationsErrored]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (orders && lastFetched && !hasTrackedPageView.current) {
      trackPageView({ page_section: PAGE_SECTIONS.MY_APIS, page_subsection: PAGE_SUBSECTIONS.OVERVIEW });
      hasTrackedPageView.current = true;
    }
  }, [orders, lastFetched]);

  const downloadSubscription = (_) => {
    const solution = orders?.app;
    setIsDownloadReportLoading(true);
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MY_APIS_SUBSCRIPTION_REPORT_DOWNLOADS,
      orgAmplitudeProperties,
    );
    axios({
      method: 'GET',
      url: `${subscriptionsUrl}/subscriptions/report`,
      params: {
        solutionId: solution?.id,
      },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        triggerUserDownload(`${solution?.name}.csv`, res.data);
        setIsDownloadReportLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsDownloadReportLoading(false);
      });
  };

  const handleShopAPIs = (redirectUrl) => {
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MY_APIS_SHOP_APIS,
      orgAmplitudeProperties,
    );
    let url = window.location.href.split(MY_APIS_URL)[0];
    localStorage.setItem('dplocationWithoutApp', `${url}/${redirectUrl}`);
    dispatch(setAPIDirectoryPage({ page: 'apiDirHome' }));
    window.location.assign(localStorage.getItem('dplocationWithoutApp'));
  };

  return (
    <Page pageHeader={PAGE_TITLE.MY_APIS}>
      <div className="main-menu-container">
        <div className="order-details-page">
          <section className="order-details-header-container">
            <div className="heading">My APIs</div>
            <div className="button-container">
              <Button
                text="Shop APIs"
                variant={BUTTON_VARIANTS.PRIMARY}
                icon={
                  <IconAddCircle
                    height={24}
                    width={24}
                    className="icon-order"
                  />
                }
                onClick={() => handleShopAPIs(API_DIR_URL)}
              />
              <Button
                text="Subscription Report"
                className="subscription-button"
                variant={BUTTON_VARIANTS.SECONDARY}
                icon={
                  <IconFileDownload
                    height={24}
                    width={24}
                    className="icon-order"
                  />
                }
                onClick={downloadSubscription}
                isDisabled={!orders?.app?.id}
                isLoading={isDownloadReportLoading}
              />
              {showToast && (
                <Toast
                  variant={toastVariant}
                  content={toastContent}
                  id="save-webhook-toast-notification"
                  onVisibilityDurationEnd={() => {
                    setShowToast(false);
                  }}
                  position={TOAST_POSITIONS.FIXED}
                  visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
                />
              )}
            </div>
          </section>
          <section>
            <OrderDetailsTabContainer />
          </section>
        </div>
      </div>
      {showCart && <MyCart setShowCart={setShowCart} />}
      <MultiToasts items={toasts} position={TOAST_POSITIONS.FIXED} />
    </Page>
  );
};

export default OrderDetails;
